import type { ProviderLegalRegistration } from "src/types/platform-legal";
import { m } from "framer-motion";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import { alpha } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import { paths } from "src/routes/paths";
import { useRouter } from "src/routes/hooks";


import { useAuthContext } from "src/auth/hooks";
import { useSnackbar } from "src/components/snackbar";
import { varHover } from "src/components/animate";

import CustomPopover, { usePopover } from "src/components/custom-popover";
import { useTranslate } from "src/locales";
import { useBoolean } from "src/hooks/use-boolean";

import { partnerProfiles } from "src/_mock/profiles";
import { useEffect, useState } from "react";
import NewRoleDialog from "src/components/newrole-dialog/newroledialog";
import { userDisplayName } from "src/utils/string-functions";
import { HOST_API } from "src/config-global";
import { useGetUserProviderProfiles } from "src/api/provider";
import { isValidToken } from "src/auth/context/jwt/utils";
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
type PopoverOption = {
  title: string;
  path: string;
  playermenudisplay: number;
  playertabsdisplay: number;
};

type PopoverOptions = {
  options: PopoverOption[];
};

export default function AccountPopover({ options }: PopoverOptions) {
  const router = useRouter();

  const { user, refreshSession, logout, addProviderRole, loading } =
    useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();

  const popover = usePopover();

  const dialog = useBoolean();

  const [hasProviderProfile, setHasProviderProfile] = useState(false);

  const { providers, providersLoading, providersError } =
    useGetUserProviderProfiles();

  // Refresh session if there's an error
  useEffect(() => {
    if (!providersLoading && providers.length > 0) {
      setHasProviderProfile(true);
    }
  }, [providers, providersLoading]);

  // useEffect(() => {
  //   if (!user?.email) {
  //     console.log("popover refreshsession");

  //     const timeout = setTimeout(() => {
  //       refreshSession();
  //     }, 1000); // 1000 ms = 1 second

  //     return () => clearTimeout(timeout); // Clean up if the component unmounts or user changes
  //   }
  // }, [user]);

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");

    if (
      !loading && // Wait until initialize() is done
      !user?.email && // Still no user
      accessToken && // There is a token
      isValidToken(accessToken) // And it’s valid
    ) {
      console.log("Popover refreshSession - forcing refresh", user);
      refreshSession();
      // const timeout = setTimeout(() => {
      //   refreshSession(); // Try to refresh session
      // }, 2000);

      // return () => clearTimeout(timeout);
    }
  }, [user, loading, refreshSession]);

  // Initialize state for checkboxes
  const [checkboxStates, setCheckboxStates] = useState(
    partnerProfiles.map((item) => ({
      checked: false,
      disabled: false,
      identifier: item.identifier,
    }))
  );

  // Handler to toggle checkbox state
  const handleCheckboxChange = (index: number) => {
    setCheckboxStates((prevStates) =>
      prevStates.map((state, i) =>
        i === index ? { ...state, checked: !state.checked } : state
      )
    );
  };

  const handleLogout = async () => {
    try {
      await logout();
      popover.onClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Unable to logout!", { variant: "error" });
    }
  };

  const handleClickItem = (path: string) => {
    popover.onClose();
    router.push(path);
  };

  const handleCreate = async (legal: ProviderLegalRegistration[] = []) => {
    const providers = checkboxStates
      .filter((item) => item.checked) // Optional: Only include checked items
      .map((item) => item.identifier); // Extract the 'identifier' property

    if (providers.length) {
      const response = await addProviderRole(providers, legal);
      router.push(paths.dashboard.root);
    }
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        {user && (
          <Avatar
            src={HOST_API + user?.photoURL}
            alt={userDisplayName(user)}
            sx={{
              width: 36,
              height: 36,
              border: (theme) =>
                `solid 2px ${theme.palette.background.default}`,
            }}
          >
            {userDisplayName(user).charAt(0).toUpperCase()}
          </Avatar>
        )}
      </IconButton>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        sx={{ width: 200, p: 0 }}
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {userDisplayName(user)}
          </Typography>

          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        <Stack sx={{ p: 1 }}>
          {options
            .filter((option) => Number(option.playermenudisplay || 0) > 0)
            .map((option) => (
              <MenuItem
                key={t(option.title)}
                onClick={() => handleClickItem(option.path)}
              >
                {t(option.title)}
              </MenuItem>
            ))}
        </Stack>

        <Divider sx={{ borderStyle: "dashed" }} />

        {!hasProviderProfile && (
          <MenuItem
            onClick={dialog.onTrue}
            sx={{ m: 1, fontWeight: "fontWeightBold", color: "info.main" }}
          >
            {t("new_role")}
          </MenuItem>
        )}

        {hasProviderProfile && (
          <MenuItem
            key={"dashboard"}
            sx={{ m: 1, fontWeight: "fontWeightBold", color: "info.main" }}
            onClick={() => handleClickItem(paths.dashboard.root)}
          >
            {t("to_dashboard")}
          </MenuItem>
        )}

        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: "fontWeightBold", color: "error.main" }}
        >
          {t("playermenu_logout")}
        </MenuItem>
      </CustomPopover>
      <NewRoleDialog
        open={dialog.value}
        onClose={dialog.onFalse}
        onCreate={handleCreate}
        partnerProfiles={partnerProfiles}
        checkboxStates={checkboxStates}
        handleCheckboxChange={handleCheckboxChange}
      />
    </>
  );
}
